import React from 'react'
import './notification-list.scss'

import { Container } from 'typescript-ioc/es5'
import * as clone from 'clone'
import * as moment from 'moment-timezone'
import deepEqual from 'react-fast-compare'

import NotificationListHeader from './notification-list-header'
import { INotificationTableData, NotificationListState } from './interfaces'
import { AppState } from '../../stores/app'
import { AccountService, AppService, DomainService, NotificationService } from '../../services'
import { NotificationScheduleService } from '../../services/notification-schedule'
import { TablePaginationConfig } from 'antd/lib/table'
import { InsightsService } from '../../services/insights'
import { Key, SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface'
import {
    BASE_DATE_FORMAT,
    BASE_TIME_ONLY_FORMAT,
    BASE_TIME_ONLY_FORMAT_WITHOUT_TZ,
    FEAT_NOTIF_SOURCE_FILTERING,
    TZ_PLATFORM_DEFAULT,
} from '../../constants'
import { NotificationDeliveryWindow } from '@pushly/models/lib/enums/notification-delivery-window'
import { AntdTableEmptyPlaceholder, Button, Drawer } from '@pushly/aqe/lib/components'
import { ClicksColumnView } from '../clicks-column-view/clicks-column-view'
import { ImpressionsColumnView } from '../impressions-column-view/impressions-column-view'
import { CtrColumnView } from '../ctr-column-view/ctr-column-view'
import { NotificationListContext } from './notification-list-context'
import { TableRowEntityDisplay } from '../table-row-entity-display/table-row-entity-display'
import { StatusBadge } from '@pushly/aqe/lib/components'
import { NotificationAbWinnerBadge } from '../badges/notification-ab-winner-badge'
import { AudienceBadge } from '../badges/audience-badge'
import { mapNotificationAudienceToBadge } from '../badges/audience-badge-mappers'
import { MultiDomainBadge } from '../badges/multi-domain-badge'
import { NotificationImageBadge } from '../badges/notification-image-badge'
import { NotificationSilentBadge } from '../badges/notification-silent-badge'
import { NotificationStzBadge } from '../badges/notification-stz-badge'
import { NotificationAbBadge } from '../badges/notification-ab-badge'
import { NotificationHoldOutBadge } from '../badges/notification-hold-out-badge'
import { ExclamationCircleFilled, FormOutlined, LoadingOutlined, WifiOutlined } from '@ant-design/icons'
import { GetNotificationScheduleResponse } from '@pushly/models/lib/structs/notifications'
import { NotificationSource } from '../../enums/notification-source'
import { HOLD_OUT_COMPUTED_STATUS, StatusType } from '../../enums/status-type'
import { stripUndefined } from '../../_utils/strip-undefined'

import { arrayContains, arrayUnique, convertCase, preventAll } from '../../_utils/utils'
import { NotificationListTypes } from './enums'
import { RefreshTimeout } from '../../enums/refresh-timeout'
import { tryParseInt } from '../../_utils/try-parse'
import { getQueryStringParam } from '../../_utils/get-query-string'
import { SizeType } from 'antd/lib/config-provider/SizeContext'
import { AdditionalDetailsDataForm } from '../../features/notifications/notification-list/additional-details-data-form'
import { onDomainIdChange } from '../../_utils/domain'
import { onAccountIdChange } from '../../_utils/account'
import { AccountNotificationService } from '../../services/account-notification'
import { OrgNotificationModel } from '../../models/notification/org-notification.model'
import OrgNotificationAudienceBadge from '../badges/org-notification-audience-badge'
import { arrayUnqiue, flatMap } from '../../_utils/array'
import OrgNotificationNotesDataForm from '../org-notification-notes-data-form/org-notification-notes-data-form'
import { parseNotificationVariantFromApiResponse } from '../../features/organizations/helpers/parse-org-notification-model'
import { SubjectEntity } from '../../enums/ability-entity.enum'
import { AbilityAction } from '../../enums/ability-action.enum'
import { asCaslSubject } from '../../stores/app-ability'
import { getTablePaginationConfig } from '../segment-list/helpers'
import { IApiResponsePaginationLinks } from '../../interfaces/api-response-pagination-links'
import SwSimpleLinkPagination from '../sw-simple-link-pagination/sw-simple-link-pagination'
import { WebBadge } from '../badges/web-badge'
import { NativeIosBadge } from '../badges/native-ios-badge'
import { NativeAndroidBadge } from '../badges/native-android-badge'
import { AiGeneratedBadge } from '../badges/ai-generated-badge'
import { ApiVersion } from '../../enums/api-version.enum'
import { NoTranslate } from '../no-translate/no-translate'
import { getNotificationAttribution } from '../../_utils/notification-attribution'
import { SwLink } from '../sw-link/sw-link'
import { addVisibilityAwareIntervalRunner, LastRunTimestampUpdater } from '../../_utils/visibility-api'
import { NotificationDeliveryType } from '../../enums/notification-delivery-type'
import { RouteComponentProps } from 'react-router-dom'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { NotificationAudienceType } from '@pushly/models/lib/enums/notification-audience-type'
import { SegmentSource } from '@pushly/models/lib/enums'
import { isNotificationEditable } from '../../features/notifications/helpers/notification-helpers'
import { UserPreferencesApi, useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { FEAT_FEEDS_MGMT } from '@pushly/aqe/lib/constants'
import { Modal, Space, Table, Tooltip } from 'antd'
import { getClassNames } from '../../_utils/classnames'

type FilterKey = keyof NotificationListState['filters']
type IdType = number | string

// bug fix type for typescript < 4.3 inferrence
type NotificationData = OrgNotificationModel | GetNotificationScheduleResponse
type TableData = INotificationTableData<OrgNotificationModel> | INotificationTableData<GetNotificationScheduleResponse>
type IterableTableData = (
    | INotificationTableData<OrgNotificationModel>
    | INotificationTableData<GetNotificationScheduleResponse>
)[]

function extractPrimaryIdFromData(data: NotificationData) {
    return data instanceof OrgNotificationModel ? data.getId()! : data.notification_id
}

function extractDeliverySpecFromData(data: NotificationData) {
    return data instanceof OrgNotificationModel
        ? convertCase(data.getDeliverySpec(), 'camel')
        : convertCase(data.delivery_spec, 'camel')
}

function extractCreatedAtFromData(data: NotificationData) {
    return data instanceof OrgNotificationModel ? data.getCreatedAt() : data.created_at
}

function extractStatusNameFromData(data: NotificationData) {
    return data instanceof OrgNotificationModel ? data.getComputedStatus() : data.status
}

function extractAbTestFromData(data: NotificationData) {
    let abTest
    if (data instanceof OrgNotificationModel) {
        const childAbTest = data.getNotifications().find((n) => n.abTestId)
        if (!data.getIsMultiDomain() && childAbTest) {
            abTest = {
                id: childAbTest.abTestId,
                name: childAbTest.abTestName,
                weight: data.getVariant()?.getWeight?.() ?? 100,
                winner: !!childAbTest.abTestWinnerOriginId,
                winnerOriginId: childAbTest.abTestWinnerOriginId,
            }
        }
    } else {
        abTest = data.ab_test
    }

    return abTest
}

function isHoldOutTest(data: NotificationData) {
    return (data as GetNotificationScheduleResponse).delivery_spec?.window === NotificationDeliveryWindow.HOLD_OUT
}

function extractChannelsFromData(data: NotificationData) {
    let channels
    if (data instanceof OrgNotificationModel) {
        channels = data.getVariant()?.getChannels()
    } else {
        channels = data.channels
    }
    return channels
}

const DEFAULT_SOURCE_FILTER = [NotificationSource.MANUAL]

const DEFAULT_STATUS_FILTER = [
    StatusType.COMPLETED.name,
    StatusType.SCHEDULED.name,
    StatusType.DELIVERING.name,
    // HOLD_OUT status is derived from the schedule's delivery spec window and not a Status ID in DB
    HOLD_OUT_COMPUTED_STATUS,
    StatusType.CANCELLED.name,
    StatusType.FAILED.name,
]

const REFRESH_QS_PARAM = '_r'
const REFRESH_OPTS_QS_PARAM = '_ropts'

interface IOrgNotificationListProps {
    level: 'org'
    orgId: number
    userId?: number
}

interface IDomainNotificationListProps {
    level: 'domain'
    domainId: number
    userId?: number
}

type NotificationListProps = Partial<RouteComponentProps<any>> &
    (IOrgNotificationListProps | IDomainNotificationListProps) & {
        title?: string
        filterSize?: SizeType
        filtersAddonBefore?: React.ReactNode
        filtersAddonAfter?: React.ReactNode
        pageSize?: number
        hidePagination?: boolean
        hideRowActions?: boolean
        hideRefresh?: boolean
        hideAutoRefreshOptions?: boolean
        autoRefreshDisabled?: boolean
        hideFilters?: FilterKey | FilterKey[] | boolean
        filtersDisabled?: FilterKey | FilterKey[] | boolean
        defaultFilters?: {
            source?: NotificationSource[]
            status?: string[]
            search?: string
            showAllNotifications?: boolean
        }
        listType: NotificationListTypes
        userPreferences?: UserPreferencesApi
    }

type State = NotificationListState & {
    dataSourcePreviousQuery?: any
    previousRowIds?: (string | number)[]
    showAddtDetailsDrawer: boolean
    addtDetailsDrawerNotifId?: number | string
    paginationLinks?: IApiResponsePaginationLinks
    showNotificationArchiveDialog: boolean
    selectedNotification: OrgNotificationModel | GetNotificationScheduleResponse | null
}

class NotificationList extends React.Component<NotificationListProps, State> {
    protected readonly appState: AppState
    protected readonly appSvc: AppService
    protected readonly orgSvc: AccountService
    protected readonly domainSvc: DomainService
    protected readonly orgNotificationSvc: AccountNotificationService
    protected readonly notificationSvc: NotificationService
    protected readonly scheduleSvc: NotificationScheduleService
    protected readonly insightsSvc: InsightsService

    protected unmounting: boolean = false
    protected refreshTimer: any
    protected updateAutoRefreshLastRunTimestamp?: LastRunTimestampUpdater
    protected disposeObservers: (() => void)[] = []

    public constructor(props: NotificationListProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appSvc = Container.get(AppService)
        this.orgSvc = Container.get(AccountService)
        this.domainSvc = Container.get(DomainService)
        this.orgNotificationSvc = Container.get(AccountNotificationService)
        this.notificationSvc = Container.get(NotificationService)
        this.scheduleSvc = Container.get(NotificationScheduleService)
        this.insightsSvc = Container.get(InsightsService)

        this.state = this.applyDefaults({
            initialLoadComplete: false,
            level: this.props.level,
            levelDependenciesLoaded: false,
            org: null!, // loaded on mount
            domain: null!, // loaded on mount

            dataSourceLoaded: false,
            dataSource: [],

            refreshing: false,
            refreshEnabled: !this.props.autoRefreshDisabled,
            refreshTimeout: RefreshTimeout.FIFTEEN_SECONDS,

            paginationConfig: getTablePaginationConfig({
                pageSize: this.props.pageSize ?? 15,
            }),

            sortedInfo: {
                columnKey: 'scheduleDate',
                order: 'descend',
            },

            segments: [],

            selectedIds: [],
            filters: {
                search: this.props.defaultFilters?.search,
                status: this.props.defaultFilters?.status ?? [
                    StatusType.CANCELLED.name,
                    StatusType.COMPLETED.name,
                    StatusType.DELIVERING.name,
                    // HOLD_OUT status is derived from the schedule's delivery spec window and not a Status ID in DB
                    HOLD_OUT_COMPUTED_STATUS,
                    StatusType.FAILED.name,
                    StatusType.SCHEDULED.name,
                ],
                showAllNotifications: this.props.defaultFilters?.showAllNotifications ?? this.props.level !== 'org',
                segments: [],
            },

            showAddtDetailsDrawer: false,
            showNotificationArchiveDialog: false,
            selectedNotification: null,
        })
    }

    public async componentDidMount() {
        let entityIdChangeHandler = this.props.level === 'domain' ? onDomainIdChange : onAccountIdChange
        this.disposeObservers.push(
            entityIdChangeHandler(this.appState, async () => {
                await this.fetchOrganizationalDependencies()
                this.resetList()
            }),
        )

        await this.fetchOrganizationalDependencies()
        this.fetchNotifications({ listType: this.props.listType })

        const [cleaner, updateTS] = addVisibilityAwareIntervalRunner(
            'notifications-list',
            async () => this.handleAutoRefresh(),
            this.state.refreshTimeout,
            true,
        )

        this.updateAutoRefreshLastRunTimestamp = updateTS
        this.disposeObservers.push(cleaner)
    }

    public componentWillUnmount() {
        this.unmounting = true
        this.disposeObservers.forEach((fn) => fn())
    }

    public render() {
        const { levelDependenciesLoaded, dataSourceLoaded, refreshing, showNotificationArchiveDialog } = this.state

        const { listType } = this.props

        const showTableLoadingAnimation = !levelDependenciesLoaded || (!dataSourceLoaded && !refreshing)

        // parse hideFilters prop for downstream
        let filtersHidden: FilterKey[] = []
        if (this.props.hideFilters === true) {
            filtersHidden = ['source', 'status', 'search', 'showAllNotifications']
        } else if (Array.isArray(this.props.hideFilters)) {
            filtersHidden = this.props.hideFilters
        } else if (this.props.hideFilters) {
            filtersHidden = [this.props.hideFilters]
        }

        const forceHideSourceFilter = this.props.level === 'org' || !this.currentDomainHasSourceFiltering(this.state)
        if (!filtersHidden.includes('source') && forceHideSourceFilter) {
            filtersHidden.push('source')
        }

        const forceHideShowAllFilter = this.props.level === 'domain'
        if (!filtersHidden.includes('showAllNotifications') && forceHideShowAllFilter) {
            filtersHidden.push('showAllNotifications')
        }

        const forceHideSegmentsFilter = this.props.level === 'org'
        if (!filtersHidden.includes('segments') && forceHideSegmentsFilter) {
            filtersHidden.push('segments')
        }

        // expose hidden options
        let showRefreshOptions: boolean | undefined
        const showRefreshOptsParams = getQueryStringParam(REFRESH_OPTS_QS_PARAM) ?? false
        if (
            showRefreshOptsParams !== undefined &&
            (showRefreshOptsParams === 'true' || showRefreshOptsParams === '1')
        ) {
            showRefreshOptions = true
        }

        const canShowActions =
            listType === NotificationListTypes.NOTIFICATION ||
            (listType === NotificationListTypes.DRAFT && this.canMutate())

        return (
            <NotificationListContext.Provider
                value={{
                    ...this.state,
                    onSearchClick: this.handleSearchClick,
                    onFilterChange: this.handleFilterChange,
                    onRefreshClick: this.handleRefreshClick,
                    // not currently utilizing refresh configs
                    onRefreshEnabledChange: () => {
                        /* noop */
                    },
                    onRefreshTimeoutChange: () => {
                        /* noop */
                    },
                }}
            >
                <Table<TableData>
                    title={(data) => (
                        <NotificationListHeader
                            title={this.props.title}
                            filterSize={this.props.filterSize}
                            filtersAddonBefore={this.props.filtersAddonBefore}
                            filtersAddonAfter={this.props.filtersAddonAfter}
                            hideFilters={filtersHidden}
                            hideRefresh={this.props.hideRefresh}
                            hideAutoRefreshOptions={
                                showRefreshOptions === undefined
                                    ? this.props.hideAutoRefreshOptions
                                    : !showRefreshOptions
                            }
                            filtersDisabled={this.props.filtersDisabled}
                            listType={listType}
                            segments={this.state.segments}
                            userId={this.appState.currentUser?.id}
                            domainId={this.props.level === 'domain' ? this.props.domainId : undefined}
                            orgId={this.props.level === 'org' ? this.props.orgId : undefined}
                        />
                    )}
                    className={getClassNames('notification-list', 'aqe-table-well')}
                    rowClassName="notification-list-row"
                    size="small"
                    dataSource={this.state.dataSource}
                    rowKey={(r) => extractPrimaryIdFromData(r.data)}
                    pagination={false}
                    footer={(currentTableData) => (
                        <SwSimpleLinkPagination
                            currentTableData={currentTableData}
                            links={this.state.paginationLinks}
                            onPrev={async (_ev, _link, config) => {
                                await this.setCurrentPage(config.current)
                                this.fetchNotifications({ listType })
                            }}
                            onNext={async (_ev, _link, config) => {
                                await this.setCurrentPage(config.current)
                                this.fetchNotifications({ listType })
                            }}
                        />
                    )}
                    onChange={this.handleTableChange}
                    locale={
                        listType === NotificationListTypes.DRAFT
                            ? {
                                  emptyText: <AntdTableEmptyPlaceholder text="No Drafts" icon={<FormOutlined />} />,
                              }
                            : {
                                  emptyText: (
                                      <AntdTableEmptyPlaceholder text="No Notifications" icon={<WifiOutlined />} />
                                  ),
                              }
                    }
                    loading={
                        showTableLoadingAnimation && {
                            indicator: <LoadingOutlined spin={true} />,
                        }
                    }
                    tableLayout="fixed"
                >
                    <Table.Column
                        key="id"
                        className="notification"
                        dataIndex={['data', 'id']}
                        title="Notification"
                        render={this.renderNotificationDisplay}
                    />

                    <Table.Column
                        key="schedule-date"
                        sorter={({ data: a }: TableData, { data: b }: TableData) => {
                            const aSpec = extractDeliverySpecFromData(a)
                            const bSpec = extractDeliverySpecFromData(b)

                            let aDate
                            let bDate
                            if (listType === NotificationListTypes.DRAFT) {
                                aDate = extractCreatedAtFromData(a)
                                bDate = extractCreatedAtFromData(b)
                            } else {
                                aDate = moment(aSpec?.sendDateUtc || extractCreatedAtFromData(a))
                                bDate = moment(bSpec?.sendDateUtc || extractCreatedAtFromData(b))
                            }

                            return aDate > bDate ? 1 : aDate < bDate ? -1 : 0
                        }}
                        defaultSortOrder="descend"
                        dataIndex={['data', 'delivery_spec']}
                        className="schedule-date"
                        title="Date"
                        align="center"
                        render={this.renderSendDate}
                        width={200}
                    />

                    {listType === NotificationListTypes.NOTIFICATION && (
                        <Table.Column
                            key="impressions"
                            className="total-impressions"
                            dataIndex={['stats', 'impressions']}
                            title="Impressions"
                            align="center"
                            render={(imps: number, { data }: TableData) => {
                                return (
                                    <ImpressionsColumnView
                                        impressions={imps}
                                        status={extractStatusNameFromData(data)}
                                    />
                                )
                            }}
                            width={120}
                        />
                    )}

                    {listType === NotificationListTypes.NOTIFICATION && (
                        <Table.Column
                            key="clicks"
                            dataIndex={['stats', 'clicks']}
                            className="total-clicks"
                            title="Clicks"
                            align="center"
                            render={(clicks: number, { data }: TableData) => {
                                return <ClicksColumnView clicks={clicks} status={extractStatusNameFromData(data)} />
                            }}
                            width={120}
                        />
                    )}

                    {listType === NotificationListTypes.NOTIFICATION && (
                        <Table.Column
                            key="ctr"
                            className="total-clicks"
                            title="CTR"
                            align="center"
                            render={(_, row: TableData) => {
                                return (
                                    <CtrColumnView
                                        impressions={row.stats.impressions}
                                        clicks={row.stats.clicks}
                                        status={extractStatusNameFromData(row.data)}
                                    />
                                )
                            }}
                            width={120}
                        />
                    )}

                    {canShowActions && (
                        <Table.Column
                            key="actions"
                            className="actions"
                            title="Actions"
                            align="right"
                            render={this.renderRowActions}
                            width={120}
                        />
                    )}
                </Table>
                {/*</div>*/}

                <Drawer
                    key="details-drawer"
                    className="notification-addt-details-drawer"
                    title="Edit Notification Details"
                    placement="right"
                    closable={true}
                    onClose={this.handleAddtDetailsDrawerClose}
                    open={this.state.showAddtDetailsDrawer}
                >
                    {this.state.showAddtDetailsDrawer &&
                        (this.props.level === 'domain' ? (
                            <AdditionalDetailsDataForm
                                key={this.state.addtDetailsDrawerNotifId}
                                getEntity={this.getCurrentDrawerData}
                                onSubmit={this.handleAddtDetailsDrawerClose}
                                onCancel={this.handleAddtDetailsDrawerClose}
                            />
                        ) : (
                            <OrgNotificationNotesDataForm
                                key={this.state.addtDetailsDrawerNotifId}
                                getEntity={this.getCurrentDrawerData}
                                onSubmit={this.handleAddtDetailsDrawerClose}
                                onCancel={this.handleAddtDetailsDrawerClose}
                            />
                        ))}
                </Drawer>

                <Modal
                    open={showNotificationArchiveDialog}
                    className="notification-list-archive-dialog"
                    title="Confirm Notification Archival"
                    okText="Archive"
                    onOk={this.handleNotificationArchive}
                    cancelText="Cancel"
                    onCancel={this.handleDismissNotificationArchiveDialog}
                >
                    <div>
                        {this.props.listType === NotificationListTypes.NOTIFICATION ? (
                            <p>
                                This notification will no longer be shown on the Notification List page. If this
                                notification has accrued any stats it will still display in Insights reports.
                            </p>
                        ) : (
                            <p>This notification draft will no longer be shown on the Notification Drafts page.</p>
                        )}
                    </div>
                </Modal>
            </NotificationListContext.Provider>
        )
    }

    protected get currentUserHasWriteAccess(): boolean {
        let userHasWriteAccess

        if (this.props.level === 'domain') {
            // Can user write to segments at the domain level?
            userHasWriteAccess = this.appState.abilityStore.can(
                AbilityAction.UPDATE,
                this.appState.abilityStore.getDomainOwnedIdentityFor(SubjectEntity.NOTIFICATION),
            )
        } else {
            const orgId = this.props.orgId
            const accessibleDomains =
                this.appState.currentUserDomains?.filter((d) => d.accountId?.toString() === orgId.toString()) ?? []

            // Can user write to segments at the org level?
            userHasWriteAccess =
                this.appState.abilityStore.can(
                    AbilityAction.UPDATE,
                    this.appState.abilityStore.getOrgOwnedIdentityFor(SubjectEntity.ORG_NOTIFICATION),
                ) && accessibleDomains.length > 1
        }

        return userHasWriteAccess
    }

    protected renderNotificationDisplay = (id: string, { data }: TableData) => {
        let canEdit = this.canMutate()
        if (this.props.level === 'org') {
            canEdit = this.canEditOrgSegment(data as OrgNotificationModel)
        }

        let isMultiDomain = false
        let containsImage = false
        let isSilent = false
        let additionalDetails
        let title
        let body

        const { listType } = this.props

        // Extract AI source and metadata
        let aiSource: string | undefined
        let aiMetadata: { reason?: string; tone?: string } | undefined

        if (data instanceof OrgNotificationModel) {
            isMultiDomain = data.getIsMultiDomain()
            additionalDetails = data.getNotes()
            title = ' '
            body = ' '

            const notifData = data.getVariant()
            if (notifData) {
                containsImage = !!notifData.getContent().getDefaultContent().getImageUrl()
                isSilent = notifData.getContent().getDefaultContent().getExperienceOptions().getIsSilent() ?? false
                title = notifData.getContent().getDefaultContent().getTitle()
                body = notifData.getContent().getDefaultContent().getBody() ?? body

                // Extract AI source and metadata for OrgNotificationModel
                const results = notifData.getContent().getDefaultContent().getAutoSuggestedFieldsResults()
                if (results) {
                    // Backward compatibility: Legacy notifications created before the source field was added
                    // may have AI-generated content (title, body, imageUrl, keywords) but no source field.
                    // In this case, default to 'page_analyzer' since it was the only AI tool available at that time.
                    if (
                        !results.source &&
                        (results.title || results.body || results.imageUrl || results.keywords?.length)
                    ) {
                        aiSource = 'page_analyzer'
                    } else {
                        aiSource = results.source ?? undefined
                    }
                    aiMetadata = results.source_metadata ?? results.sourceMetadata ?? undefined
                }
            }
        } else {
            isMultiDomain = data.notification_group?.is_multi_domain ?? false
            additionalDetails = data.additional_details
            containsImage = !!data.template?.channels?.default?.image_url
            isSilent = data.template?.channels?.default?.is_silent ?? false
            title = data.template?.channels?.default?.title
            body = data.template?.channels?.default?.body

            // Extract AI source and metadata for GetNotificationScheduleResponse
            const results = data.template?.channels?.default?.auto_suggested_fields_results as any
            if (results) {
                // Backward compatibility: Legacy notifications created before the source field was added
                // may have AI-generated content (title, body, imageUrl, keywords) but no source field.
                // In this case, default to 'page_analyzer' since it was the only AI tool available at that time.
                if (
                    !results.source &&
                    (results.title || results.body || results.imageUrl || results.keywords?.length)
                ) {
                    aiSource = 'page_analyzer'
                } else {
                    aiSource = results.source ?? undefined
                }
                aiMetadata = results.source_metadata ?? results.sourceMetadata ?? undefined
            }
        }

        const abTest = extractAbTestFromData(data)
        const deliverySpec = extractDeliverySpecFromData(data)
        const isStz = deliverySpec?.window === NotificationDeliveryWindow.TIMEZONE

        const channels = extractChannelsFromData(data)

        const isDomainLevel = this.props.level === 'domain'
        const showMultiDomainBadge =
            isMultiDomain &&
            (isDomainLevel || this.state.filters.showAllNotifications) &&
            this.appState.abilityStore.can(
                AbilityAction.READ,
                this.appState.abilityStore.getOrgOwnedIdentityFor(SubjectEntity.ORG_NOTIFICATION),
            )

        // Feed attribution — domain notification list only (org notifications are excluded).
        // Non-feed rows preserve their existing behavior (no attribution line).
        let feedAttribution: React.ReactNode = null
        if (isDomainLevel && !(data instanceof OrgNotificationModel)) {
            const attribution = getNotificationAttribution({
                source: data.source,
                feed: data.feed,
            })
            if (attribution.isFeed) {
                feedAttribution = (
                    <span className="notification-list-feed-attribution" title={attribution.text}>
                        {attribution.prefix}
                        {attribution.value !== undefined && <NoTranslate>{attribution.value}</NoTranslate>}
                    </span>
                )
            }
        }

        return (
            <TableRowEntityDisplay
                title={
                    <NoTranslate>
                        {listType === NotificationListTypes.DRAFT ? (
                            <a className="notification-draft-name">{title}</a>
                        ) : canEdit ? (
                            <SwLink href={this.buildDetailsUrl(data)}>{title}</SwLink>
                        ) : (
                            title
                        )}
                    </NoTranslate>
                }
                description={<NoTranslate>{body}</NoTranslate>}
                status={
                    <StatusBadge
                        type="notification"
                        status={
                            listType === NotificationListTypes.NOTIFICATION
                                ? extractStatusNameFromData(data)
                                : StatusType.DRAFT.name
                        }
                        expanded={true}
                        holdOut={isHoldOutTest(data)}
                    />
                }
                test={
                    abTest && (
                        <>
                            {abTest.winner && <NotificationAbWinnerBadge />}
                            <span className="ab-name">
                                {listType === NotificationListTypes.DRAFT ? (
                                    <a className="notification-draft-name">{abTest.name}</a>
                                ) : (
                                    <SwLink href={this.buildTestDetailsUrl(data)}>{abTest.name}</SwLink>
                                )}
                            </span>
                        </>
                    )
                }
                audience={
                    <span className="audience-type">
                        {data instanceof OrgNotificationModel ? (
                            <OrgNotificationAudienceBadge source={data} />
                        ) : (
                            <AudienceBadge audience={mapNotificationAudienceToBadge(data.audience)} />
                        )}
                    </span>
                }
                badges={
                    <Space size="small">
                        {feedAttribution}
                        {showMultiDomainBadge && <MultiDomainBadge entityLabel="Notification" />}
                        {containsImage && <NotificationImageBadge />}
                        {isSilent && <NotificationSilentBadge />}
                        {isStz && <NotificationStzBadge />}
                        {abTest && <NotificationAbBadge />}
                        {isHoldOutTest(data) && <NotificationHoldOutBadge />}
                        {channels?.includes(DeliveryChannel.WEB) && <WebBadge />}
                        {channels?.includes(DeliveryChannel.NATIVE_IOS) && <NativeIosBadge />}
                        {channels?.includes(DeliveryChannel.NATIVE_ANDROID) && <NativeAndroidBadge />}
                        {aiSource === 'pushly_ai' && (
                            <AiGeneratedBadge source={aiSource} sourceMetadata={aiMetadata} showMetadata={false} />
                        )}

                        {/* Don't add anything below this line */}
                        {!!additionalDetails ? (
                            <span>
                                <Tooltip title={additionalDetails}>
                                    <ExclamationCircleFilled className="additional-details fg--color--orange-100" />
                                </Tooltip>
                            </span>
                        ) : (
                            <></>
                        )}
                    </Space>
                }
            />
        )
    }

    protected renderSendDate = (_: any, { data }: TableData) => {
        const { listType } = this.props
        const isDraft = listType === NotificationListTypes.DRAFT

        const deliverySpec = extractDeliverySpecFromData(data)
        const isStz = deliverySpec?.window === NotificationDeliveryWindow.TIMEZONE
        const isInf = deliverySpec?.window === NotificationDeliveryWindow.INFORMED
        const isStd = deliverySpec?.window === NotificationDeliveryWindow.STANDARD
        const isHoldOut = deliverySpec?.window === NotificationDeliveryWindow.HOLD_OUT
        const isImmediate = deliverySpec?.type === NotificationDeliveryType.IMMEDIATE
        const isImmediateDraft = isDraft && isImmediate

        let displayTz = TZ_PLATFORM_DEFAULT
        if (this.state.level === 'domain' && this.state.domain?.timezone) {
            displayTz = this.state.domain.timezone
        } else if (this.state.level === 'org' && this.state.org?.timezone) {
            displayTz = this.state.org.timezone ?? displayTz
        }

        const sendDateTz = deliverySpec?.timezone ?? displayTz
        const sendDateUtc = deliverySpec?.sendDateUtc ?? (data as any).sendDateUtc
        const sendDate = moment.tz(sendDateUtc, sendDateTz)

        // stz sends should keep original send/time
        if (!isStz) {
            sendDate.tz(displayTz)
        }

        return (
            <div className="composite-stack">
                {isImmediateDraft ? <span>Immediate</span> : <span>{sendDate.format(BASE_DATE_FORMAT)}</span>}
                {!isImmediateDraft && (
                    <span className="lower">
                        {isStz && (
                            <Space>
                                {sendDate.format(BASE_TIME_ONLY_FORMAT_WITHOUT_TZ)}
                                <span className="stz-label">
                                    <Tooltip title="Subscriber's Time Zone">STZ</Tooltip>
                                </span>
                            </Space>
                        )}
                        {isInf && (
                            <Space>
                                {sendDate.format(BASE_TIME_ONLY_FORMAT_WITHOUT_TZ)}
                                <span className="stz-label">
                                    <Tooltip title="Informed Delivery">INF</Tooltip>
                                </span>
                            </Space>
                        )}
                        {(isStd || isHoldOut) && sendDate.format(BASE_TIME_ONLY_FORMAT)}
                    </span>
                )}
            </div>
        )
    }

    protected renderRowActions = (
        _,
        tableDto:
            | INotificationTableData<OrgNotificationModel>
            | INotificationTableData<GetNotificationScheduleResponse>,
    ): React.ReactNode => {
        const { listType } = this.props

        const isOrgLevel = this.props.level === 'org'

        const isInternalNonReadOnlyUser = this.appState.currentUser?.isInternalUser === true && this.canMutate()

        const canMutate = this.canMutate()
        let canEditOrgNotif = canMutate
        if (this.props.level === 'org') {
            canEditOrgNotif = this.canEditOrgSegment(tableDto.data as OrgNotificationModel)
        }

        const data = tableDto.data
        const deliverySpec = extractDeliverySpecFromData(data)
        const abTest = extractAbTestFromData(data)

        let statusTypeKey: string
        if (data instanceof OrgNotificationModel) {
            statusTypeKey = data.getComputedStatus()?.toUpperCase?.() ?? StatusType.SCHEDULED.name
        } else {
            statusTypeKey = data.status.toUpperCase()
        }

        // Build a shape compatible with isNotificationEditable
        const editabilityShape = {
            status: statusTypeKey,
            delivery_spec: data instanceof OrgNotificationModel ? data.getDeliverySpec() : data.delivery_spec,
        }
        const editable = isNotificationEditable(editabilityShape as any)

        const computedStatus = StatusType[statusTypeKey]
        const isScheduledPush = computedStatus.id === StatusType.SCHEDULED.id
        const isSchedulingPush = computedStatus.id === StatusType.SCHEDULING.id
        const isDeliveringPush = computedStatus.id === StatusType.DELIVERING.id

        const isInfWindow = deliverySpec?.window === NotificationDeliveryWindow.INFORMED
        const isDeliveringInfPush = isInfWindow && (isDeliveringPush || isSchedulingPush)

        const isCancellable = isDeliveringInfPush || editable

        let actions: any = []

        if (canMutate) {
            if ((!isOrgLevel || canEditOrgNotif) && editable) {
                actions.push({
                    text: 'Edit',
                    icon: 'edit',
                    onClick: this.handleNotificationEdit,
                    data,
                    altHref: this.buildEditUrl(data),
                })
            }

            actions.push({
                text: 'Duplicate',
                icon: 'copy',
                onClick: this.handleDuplicate,
                data,
                altHref: this.buildDuplicateUrl(data),
            })
        }

        if (abTest) {
            if (canMutate) {
                actions.push({ divider: true })
            }

            if (listType === NotificationListTypes.NOTIFICATION) {
                actions.push({
                    text: 'View Test',
                    icon: 'eye',
                    onClick: this.handleJumpToTestDetails,
                    data,
                    altHref: this.buildTestDetailsUrl(data),
                })
            }

            if (canMutate) {
                actions.push({
                    text: 'Duplicate Test',
                    icon: 'copy',
                    onClick: this.handleDuplicateTest,
                    data,
                    altHref: this.buildDuplicateUrl(data, true),
                })
            }
        }

        if (canMutate && isCancellable && (!isOrgLevel || canEditOrgNotif)) {
            actions.push({ divider: true })
            actions.push({
                text: !!abTest ? 'Cancel Test' : 'Cancel',
                icon: 'close',
                onClick: this.handleCancel,
                data,
            })
        }

        const canUserArchive =
            isInternalNonReadOnlyUser ||
            this.appState.abilityStore.can(AbilityAction.UPDATE, this.appState.abilityStore.currentDomainIdentity)

        if (this.state.level === 'domain' && canUserArchive) {
            if (actions.length) {
                actions.push({ divider: true })
            }

            actions.push({
                text: 'Archive',
                icon: 'folder',
                onClick: this.handleShowNotificationArchiveDialog,
                data,
                altHref: this.buildArchiveUrl(data),
            })
        }

        if (isInternalNonReadOnlyUser && listType === NotificationListTypes.NOTIFICATION) {
            if (actions.length) {
                actions.push({ divider: true })
            }

            actions.push({
                text: 'Set Note',
                icon: 'edit',
                onClick: () => this.toggleAddtDetailsDrawer(data),
                data,
            })
        }

        if (actions.length === 0 || this.props.hideRowActions === true) {
            actions = undefined
        }

        return (
            <Button
                actions={actions}
                onClick={() => this.handlePrimaryActionClick(data, listType)}
                size="small"
                altHref={this.buildDetailsUrl(data)}
            >
                {listType === NotificationListTypes.NOTIFICATION ? <span>View</span> : <span>Edit</span>}
            </Button>
        )
    }

    protected getAbilityEntity() {
        return this.props.level === 'org' ? SubjectEntity.ORG_NOTIFICATION : SubjectEntity.NOTIFICATION
    }

    protected getAbilityConstraint() {
        return this.props.level === 'org' ? { accountId: this.props.orgId } : { domainId: this.props.domainId }
    }

    protected canMutate() {
        return this.appState.abilityStore.can(
            AbilityAction.UPDATE,
            asCaslSubject(this.getAbilityEntity(), this.getAbilityConstraint()),
        )
    }

    protected canEditOrgSegment(model: OrgNotificationModel) {
        const canEdit =
            this.props.level === 'domain'
                ? false
                : !model
                      .getNotifications()
                      .some(
                          (n) =>
                              !this.appState.abilityStore.can(
                                  AbilityAction.UPDATE,
                                  asCaslSubject(SubjectEntity.SEGMENT, { domainId: n.domainId }),
                              ),
                      )

        return canEdit
    }

    protected currentDomainHasSourceFiltering(state: State): boolean {
        let hasFlag = false

        if (state.level === 'domain' && state.domain) {
            const notifSourceFlag = this.appState.flags.findActive(FEAT_NOTIF_SOURCE_FILTERING)?.getKey()
            hasFlag = !!notifSourceFlag && state.domain.flags?.includes(notifSourceFlag)
        }

        return hasFlag
    }

    protected getDefaultSourceFilter(state: State): string[] {
        let source = this.appState.flags.findActive(FEAT_FEEDS_MGMT)
            ? [...DEFAULT_SOURCE_FILTER, NotificationSource.FEED]
            : DEFAULT_SOURCE_FILTER

        // callees include constructor so state is not guaranteed
        if (this.props.level === 'domain') {
            const domainSourceFilters = this.props.userPreferences?.get(['notification_source_filters'])
            if (this.currentDomainHasSourceFiltering(state) && domainSourceFilters?.length) {
                source = domainSourceFilters
            }

            if (source.length === 0 && this.props.defaultFilters?.source) {
                source = this.props.defaultFilters.source
            }
        }

        return source
    }

    protected getDefaultStatusFilter(): string[] {
        let status = DEFAULT_STATUS_FILTER

        if (this.props.level === 'domain') {
            const domainStatusFilters = this.props.userPreferences?.get(['notification_status_filters'])
            if (domainStatusFilters?.length) {
                status = domainStatusFilters
            }

            if (status.length === 0 && this.props.defaultFilters?.status) {
                status = this.props.defaultFilters.status
            }
        }

        return status
    }

    protected getDefaultSegmentIdsFilter(): { value: number; label: string }[] {
        let segments: { value: number; label: string }[] = []

        if (this.props.level === 'domain') {
            const domainSegmentsFilters = this.props.userPreferences?.get(['notification_segment_filters'])

            if (domainSegmentsFilters?.length) {
                segments = domainSegmentsFilters.map((obj) => {
                    const [[key, label]] = Object.entries(obj)
                    return { value: Number(key), label }
                })
            }
        }

        return segments
    }

    protected getCurrentDrawerData = <T extends NotificationData | undefined>(): T => {
        const currDataSource: IterableTableData = this.state.dataSource

        const match = currDataSource.find(({ data }) => {
            return this.state.addtDetailsDrawerNotifId === extractPrimaryIdFromData(data)
        })

        return match?.data as T
    }

    protected buildTestDetailsUrl(data: OrgNotificationModel | GetNotificationScheduleResponse): string {
        const abTestId = extractAbTestFromData(data)?.id

        const newRoute = `/notifications/test/${abTestId}/summary`
        return this.appSvc.routeWithin(this.props.level, newRoute, true)
    }

    protected buildDetailsUrl(data: OrgNotificationModel | GetNotificationScheduleResponse): string {
        const newRoute = `/notifications/${extractPrimaryIdFromData(data)}/summary`
        return this.appSvc.routeWithin(this.props.level, newRoute, true)
    }

    protected buildDuplicateUrl(
        data: OrgNotificationModel | GetNotificationScheduleResponse,
        withTest: boolean = false,
    ): string {
        const abTestId = extractAbTestFromData(data)?.id

        let newRoute = `/notifications/new?notification_id=${extractPrimaryIdFromData(data)}`
        if (withTest && abTestId) {
            newRoute = `${newRoute}&test=${abTestId}`
        }

        return this.appSvc.routeWithin(this.props.level, newRoute, true)
    }

    protected buildEditUrl(data: OrgNotificationModel | GetNotificationScheduleResponse): string {
        let notifId: IdType
        let abTestId: IdType | undefined

        if (data instanceof OrgNotificationModel) {
            notifId = data.getId()!
        } else {
            notifId = data.notification_id
            abTestId = data.ab_test?.id
        }

        let newRoute = `/notifications/edit/${notifId}`
        if (this.props.level === 'org') {
            newRoute = `/notifications/${notifId}/edit`
        }

        if (abTestId) {
            newRoute = `${newRoute}?test=${abTestId}`
        }

        return this.appSvc.routeWithin(this.props.level, newRoute, true)
    }

    protected buildArchiveUrl(data: OrgNotificationModel | GetNotificationScheduleResponse): string {
        const newRoute = `/notifications/${extractPrimaryIdFromData(data)}`
        return this.appSvc.routeWithin(this.props.level, newRoute, true)
    }

    protected async setStateAsync<K extends keyof State>(
        state:
            | ((prevState: Readonly<State>, props: Readonly<NotificationListProps>) => Pick<State, K> | State | null)
            | (Pick<State, K> | State | null),
    ): Promise<void> {
        return new Promise((res) => {
            if (!this.unmounting) {
                this.setState(state as State, res)
            }
        })
    }

    protected applyDefaults(state: State): State {
        // default query
        const query = getQueryStringParam('query')
        if (!!query?.trim()) {
            state.filters = {
                ...state.filters,
                search: query.trim(),
            }
        }

        // default refreshTimer
        const refresh = getQueryStringParam(REFRESH_QS_PARAM, tryParseInt)
        if (refresh !== undefined && !isNaN(refresh)) {
            state.refreshTimeout = refresh * 1000
            if (refresh === 0) {
                state.refreshEnabled = false
            }
        }

        // ensure default page
        const currentPage = getQueryStringParam('page', tryParseInt)
        if (currentPage && currentPage !== 1) {
            state.paginationConfig = {
                ...state.paginationConfig,
                current: currentPage,
            }
        }

        if (this.props.level === 'domain') {
            state = this.applyDefaultFilters(state)
        }

        return state
    }

    protected applyDefaultFilters(state: State) {
        // ensure default source filter from user state
        state.filters = {
            ...(state.filters ?? this.state?.filters ?? {}),
            source: this.props.defaultFilters?.source ?? this.getDefaultSourceFilter(state),
            status: this.props.defaultFilters?.status ?? this.getDefaultStatusFilter(),
            segments: this.getDefaultSegmentIdsFilter(),
        }

        return state
    }

    protected updatePathParams(): void {
        const {
            paginationConfig: { current: page },
        } = { ...this.state }

        let parsedUpdate: string | undefined

        if (page !== 1) {
            parsedUpdate = `&page=${page}`
        }

        const refresh = getQueryStringParam(REFRESH_QS_PARAM)
        if (refresh !== undefined) {
            parsedUpdate = `${parsedUpdate ?? ''}&${REFRESH_QS_PARAM}=${refresh}`
        }

        const refreshOpts = getQueryStringParam(REFRESH_OPTS_QS_PARAM)
        if (refreshOpts !== undefined) {
            parsedUpdate = `${parsedUpdate ?? ''}&${REFRESH_OPTS_QS_PARAM}=${refreshOpts}`
        }

        this.props.history?.push?.({ search: parsedUpdate?.replace(/^&/, '') })
    }

    protected async resetList() {
        await this.setStateAsync({ dataSourceLoaded: false })

        await this.setStateAsync((curr) => ({
            previousRowIds: [],
            paginationConfig: {
                ...curr.paginationConfig,
                current: 1,
            },
            filters: {
                ...curr.filters,
                source: this.getDefaultSourceFilter(curr),
                status: this.getDefaultStatusFilter(),
                segmentIds: this.getDefaultSegmentIdsFilter(),
                search: undefined,
            },
        }))

        this.fetchNotifications({ forceFetch: true, listType: this.props.listType })
    }

    protected async setCurrentPage(page: number | undefined): Promise<void> {
        await this.setStateAsync(({ paginationConfig }) => ({
            paginationConfig: {
                ...paginationConfig,
                current: page ?? 1,
            },
        }))

        this.updatePathParams()
    }

    protected handleTableChange = async (
        pagination: TablePaginationConfig,
        _filters: Record<string, Key[] | null>,
        _sorter: SorterResult<TableData> | SorterResult<TableData>[],
        _extra: TableCurrentDataSource<TableData>,
    ): Promise<void> => {
        await this.setCurrentPage(pagination.current)
        this.fetchNotifications({ listType: this.props.listType })
    }

    protected handleSearchClick = async (value: string) => {
        const prevFilter = this.state.filters.search

        await this.setStateAsync(({ filters }) => ({
            filters: {
                ...filters,
                search: value,
            },
        }))

        const emptyValues = !value.trim() && !prevFilter?.trim()
        if (!emptyValues) {
            await this.setCurrentPage(1)
            this.fetchNotifications({
                forceFetch: true,
                listType: this.props.listType,
            })
        }
    }

    protected handleFilterChange = async <FilterValue extends NotificationListState['filters'][FilterKey]>(
        key: FilterKey,
        value: FilterValue,
    ): Promise<void> => {
        const prevFilters = clone(this.state.filters)

        if (Array.isArray(value) && (key === 'status' || key === 'source')) {
            value.sort()
        }

        if (this.state.level === 'domain' && key === 'source') {
            this.props.userPreferences?.setDebounced(['notification_source_filters'], value)
        }

        if (this.state.level === 'domain' && key === 'status') {
            this.props.userPreferences?.setDebounced(['notification_status_filters'], value)
        }

        if (this.state.level === 'domain' && key === 'segments') {
            const storedValue: { [id: number]: string } = {}
            this.state.segments
                .filter((s) => (value as { label: string; value: number }[]).find((v) => v.value === s.id))
                .forEach((sv) => {
                    storedValue[sv.id] = sv.name
                })

            this.props.userPreferences?.setDebounced(
                ['notification_segment_filters'],
                Object.entries(storedValue).map(([k, v]) => ({ [k]: v })),
            )
        }

        await this.setStateAsync(({ filters }) => ({
            filters: {
                ...filters,
                [key]: value,
            },
        }))

        if (!deepEqual(this.state.filters, prevFilters)) {
            // ensure table load animation shows after filter change
            await this.setStateAsync({ refreshing: false })

            await this.setCurrentPage(1)
            this.fetchNotifications({ listType: this.props.listType })
        }
    }

    protected handleRefreshClick = async (_ev: React.MouseEvent<HTMLElement>): Promise<void> => {
        // manual refresh click should not set refreshing
        // to ensure full table loading animation is shown
        await this.fetchNotifications({
            forceFetch: true,
            listType: this.props.listType,
        })

        this.updateAutoRefreshLastRunTimestamp?.()
    }

    protected handlePrimaryActionClick = (
        data: NotificationData,
        listType: NotificationListTypes = NotificationListTypes.NOTIFICATION,
    ): void => {
        if (listType === NotificationListTypes.NOTIFICATION) {
            this.appSvc.route(this.buildDetailsUrl(data))
        } else {
            this.appSvc.route(this.buildEditUrl(data))
        }
    }

    protected handleDuplicate = (item: any) => {
        this.appSvc.route(this.buildDuplicateUrl(item.props.data))
    }

    protected handleDuplicateTest = (item: any) => {
        this.appSvc.route(this.buildDuplicateUrl(item.props.data, true))
    }

    protected handleJumpToTestDetails = (item: any, ev: any): void => {
        if (!ev || !ev.metaKey) {
            preventAll(ev)
            this.appSvc.routeWithinDomain(this.buildTestDetailsUrl(item.props.data))
        }
    }

    protected handleNotificationEdit = (item: any) => {
        this.appSvc.route(this.buildEditUrl(item.props.data))
    }

    protected handleCancel = async (item: any): Promise<void> => {
        const { data } = item.props

        const refreshOptions = {
            showLoading: true,
            forceFetch: true,
            listType: this.props.listType,
        }

        if (this.state.level === 'domain') {
            if (data.ab_test?.id) {
                await this.notificationSvc.cancelTest(this.state.domain.id, data.ab_test.id)
            } else {
                await this.scheduleSvc.cancelSchedule(data)
            }
        } else {
            await this.orgNotificationSvc.cancel(data, {
                showLoadingScreen: true,
            })
        }

        this.fetchNotifications(refreshOptions)
    }

    protected handleDismissNotificationArchiveDialog = () => {
        return this.setState({
            showNotificationArchiveDialog: false,
            selectedNotification: null,
        })
    }

    protected handleShowNotificationArchiveDialog = (item: any) => {
        return this.setState({
            showNotificationArchiveDialog: true,
            selectedNotification: item.props.data,
        })
    }

    protected handleNotificationArchive = async (): Promise<void> => {
        this.handleDismissNotificationArchiveDialog()
        if (this.state.selectedNotification) {
            const notificationId = extractPrimaryIdFromData(this.state.selectedNotification)

            const refreshOptions = {
                showLoading: true,
                forceFetch: true,
                listType: this.props.listType,
            }

            if (this.state.level === 'domain') {
                await this.notificationSvc.archiveNotification(this.state.domain.id, Number(notificationId))
            }
            this.fetchNotifications(refreshOptions)
        }
    }

    protected toggleAddtDetailsDrawer = (data: NotificationData) => {
        return this.setStateAsync({
            showAddtDetailsDrawer: true,
            addtDetailsDrawerNotifId: extractPrimaryIdFromData(data),
        })
    }

    protected handleAddtDetailsDrawerClose = () => {
        return this.setStateAsync({
            showAddtDetailsDrawer: false,
            addtDetailsDrawerNotifId: undefined,
        })
    }

    protected handleAutoRefresh = async () => {
        if (!this.unmounting && this.state.refreshEnabled) {
            // prevents full table loading animation
            await this.setStateAsync({ refreshing: true })
            await this.fetchNotifications({ forceFetch: true, listType: this.props.listType })
        }
    }

    protected async fetchNotifications(opts?: { listType?: NotificationListTypes; forceFetch?: boolean }) {
        // reset auto refresh -- this is a hack to ensure the table does not auto cancel and retry during a manual update
        this.updateAutoRefreshLastRunTimestamp?.()

        const { filters } = this.state

        const isDraftList = opts?.listType === NotificationListTypes.DRAFT
        const defaultAllStatuses = ['CANCELLED', 'COMPLETED', 'DELIVERING', 'FAILED', 'HOLD_OUT', 'SCHEDULED']
        const statusFilters = isDraftList
            ? [StatusType.DRAFT.name]
            : Array.from(filters.status?.length === 0 ? defaultAllStatuses : (filters.status ?? []))

        if (arrayContains(statusFilters, 'COMPLETED')) {
            statusFilters.push('COMPLETED_WITH_FAILURES')
        }

        let sourceFilters = Array.from(filters.source ?? [])
        if (this.props.level === 'org' || isDraftList) {
            sourceFilters = []
        }

        const filterSegmentIdsRequested = filters.segments ?? []
        const isAllSegmentIdsRequest =
            filterSegmentIdsRequested.length === 0 || filterSegmentIdsRequested.length === this.state.segments.length

        const apiOptions = {
            showLoadingScreen: false,
            cancellationKey: 'notif-list.fetch',
            query: stripUndefined({
                page: this.state.paginationConfig.current,
                limit: this.state.paginationConfig.pageSize,
                include_segments: 1,
                search: filters.search ?? undefined,
                source: sourceFilters.length > 0 ? sourceFilters.join(',') : undefined,
                status: statusFilters.length > 0 ? statusFilters.join(',') : undefined,
                segment_ids: isAllSegmentIdsRequest
                    ? undefined
                    : filterSegmentIdsRequested.map((s) => s.value).join(','),
                multi_domain_only: !filters.showAllNotifications,
                audience_types: [NotificationAudienceType.SEGMENTED].join(','),
            }),
            version: ApiVersion.V4,
        }

        // Abort fetch if manual refresh and query has not changed
        const sameQuery =
            !this.state.refreshing &&
            this.state.dataSourcePreviousQuery &&
            deepEqual(apiOptions.query, this.state.dataSourcePreviousQuery)
        if (!opts?.forceFetch && sameQuery) {
            return
        }

        const currDataSource: IterableTableData = this.state.dataSource

        // Start loading animation
        await this.setStateAsync({
            dataSourceLoaded: false,
            dataSourcePreviousQuery: apiOptions.query,
            previousRowIds: currDataSource?.map(({ data }) => {
                return (data instanceof OrgNotificationModel ? data.getId() : data.id) as string | number
            }),
        })

        let nextDataSource: TableData[] = []
        const state: any = {
            ...this.state,
            refreshing: false,
            dataSourceLoaded: true,
        }

        let resolver
        if (this.props.level === 'domain') {
            resolver = this.scheduleSvc.fetchAllByDomainId(this.props.domainId, apiOptions)
        } else {
            resolver = this.orgNotificationSvc.fetchAll(this.props.orgId, apiOptions).then((r) => {
                if (r.ok && r.data) {
                    r.data = r.data.map((n) => {
                        const model = OrgNotificationModel.build(n)
                        model.setVariant(parseNotificationVariantFromApiResponse(n))
                        return model
                    })
                }

                return r
            })
        }

        const res = await Promise.resolve(resolver)
        if (!res.cancelled) {
            if (res.ok && res.data) {
                res.data.forEach((datum) => {
                    nextDataSource.push({
                        data: datum,
                        stats: {},
                    })
                })

                if (nextDataSource.length > 0) {
                    nextDataSource = await this.fetchStats(nextDataSource)
                }

                state.paginationLinks = res.meta.links ?? state.paginationLinks
                state.dataSource = nextDataSource
            } else if (res.error) {
                console.warn(res.error)
            }

            await this.setStateAsync(state)
        }

        this.updateAutoRefreshLastRunTimestamp?.()
    }

    protected async fetchStats(dataSource: TableData[]): Promise<TableData[]> {
        const dataSourceWithStats = clone(dataSource)

        let domainIds
        let notifIdFilterField
        let notifIds
        let extractPrimaryIdFromStats: (_: any) => string
        const breakdowns: string[] = []
        const notifFields: string[] = []

        if (this.props.level === 'domain') {
            domainIds = [this.props.domainId]

            extractPrimaryIdFromStats = (s) => s.notification.id
            notifIdFilterField = 'notification.id'
            notifIds = arrayUnique(
                dataSource.map((d: INotificationTableData<GetNotificationScheduleResponse>) => {
                    return d.data.notification_id
                }),
            )

            breakdowns.push('notification')
            notifFields.push(notifIdFilterField)
        } else {
            domainIds = arrayUnqiue(
                flatMap(dataSource, (d: INotificationTableData<OrgNotificationModel>) => {
                    return d.data.getNotifications()
                }).map((n) => n.domainId),
            )

            extractPrimaryIdFromStats = (s) => s.notification_group.id
            notifIdFilterField = 'notification_group.id'
            notifIds = arrayUnqiue(
                dataSource.map((d: INotificationTableData<OrgNotificationModel>) => {
                    return d.data.getId()
                }),
            )

            breakdowns.push('notification_group')
            notifFields.push(notifIdFilterField)
        }

        const insightsPkg: any = {
            entity: 'notifications',
            date_preset: 'lifetime',
            date_increment: 'lifetime',
            breakdowns,
            fields: [
                ...notifFields,
                'deliveries',
                'impressions',
                'clicks',
                'ctr',
                'ctr_decimal',
                'delivery_rate',
                'delivery_rate_decimal',
            ],
            filters: [
                {
                    field: 'domain.id',
                    operator: 'in',
                    value: domainIds,
                },
                {
                    field: notifIdFilterField,
                    operator: 'in',
                    value: notifIds,
                },
            ],
        }

        if (this.props.level === 'domain') {
            insightsPkg.action_attribution = 'send_time'
        }

        const stats = await this.insightsSvc.fetch(insightsPkg, {
            requestScope: this.props.level === 'org' ? 'organizations' : 'domains',
            scopeId: this.props.level === 'org' ? this.props.orgId : this.props.domainId,
            nestedScope: 'notifications',
            cancellationKey: 'nl.stats',
        })

        if (stats?.find) {
            dataSourceWithStats.forEach((datum) => {
                const stat = stats.find(
                    (s: any) => String(extractPrimaryIdFromStats(s)) === String(extractPrimaryIdFromData(datum.data)),
                )

                if (stat) {
                    datum.stats.deliveries = stat.deliveries
                    datum.stats.impressions = stat.impressions
                    datum.stats.clicks = stat.clicks
                    datum.stats.ctr = stat.ctr
                    datum.stats.ctr_decimal = stat.ctr_decimal
                    datum.stats.delivery_rate = stat.delivery_rate
                    datum.stats.delivery_rate_decimal = stat.delivery_rate_decimal
                }
            })
        }

        return dataSourceWithStats
    }

    protected async fetchOrganizationalDependencies() {
        let state: any = { levelDependenciesLoaded: false }
        await this.setStateAsync({ ...state })

        if (this.props.level === 'domain') {
            const { ok: domainFetchOk, data } = await this.domainSvc.fetchById(this.props.domainId)
            if (domainFetchOk && data) {
                state.domain = data
            }

            const { ok: segmentFetchOk, data: segments } = await this.domainSvc.fetchSegmentsByDomainId(
                this.props.domainId,
                {
                    query: {
                        includeTreated: 0,
                        pagination: 0,
                        fields: 'id,name,source,computedStatus,isDefault,iconUrl,flags,lastTargetedDateUtc',
                        source: SegmentSource.STANDARD,
                    },
                },
            )

            if (segmentFetchOk && segments.length) {
                state.segments = segments
            }

            state.level = 'domain' // required for flag check
            state = this.applyDefaultFilters(state)
        } else {
            state.org = await this.orgSvc.fetchById(this.props.orgId)
        }

        state.levelDependenciesLoaded = true
        if (!this.state.initialLoadComplete) {
            state.initialLoadComplete = true
        }

        await this.setStateAsync(state)
    }
}

const NotificationListWrapper = (props: NotificationListProps) => {
    const userPreferences = useUserPreferences(
        props.userId,
        props.level === 'domain' ? props.domainId : undefined,
        props.level === 'org' ? props.orgId : undefined,
    )

    return <NotificationList {...props} userPreferences={userPreferences} />
}

export default NotificationListWrapper
